import { useCallback, useEffect, useMemo, useState } from "react";

import { computeShouldShowMicUnverifiedPrompt } from "features/medical-scribe/microphone-verification.util";
import {
  type MicrophoneAssessment,
  type MicrophoneFingerprint,
  type MicrophoneInput,
  MicrophoneVerificationStatus,
  PSEUDO_DEVICE_IDS,
  assessMicrophone,
  createMicrophoneFingerprint,
  fingerprintToInput,
  requestMicrophonePermissionForEnumeration
} from "features/medical-scribe/microphone.utils";
import {
  getVerifiedMicrophones,
  saveVerifiedMicrophone
} from "features/medical-scribe/microphone-storage";
import { useGetAmbientScribesQuery } from "services/ambient-scribe";

const LAST_SCRIBE_QUERY = "{id,status,created_at,microphone_data}";

export type PrepareAmbientScribeStartResult =
  | { cancelled: true }
  | {
      cancelled?: false;
      needsMicrophoneSetup: true;
      shouldShowMicUnverifiedPrompt: false;
    }
  | {
      cancelled?: false;
      needsMicrophoneSetup?: false;
      shouldShowMicUnverifiedPrompt: boolean;
      microphoneFingerprint?: MicrophoneInput;
    };

export function useMicrophoneAssessment(
  deviceId: string,
  isEnabled: boolean,
  staffId: number | undefined,
  getMicSetupCompletedAt?: () => string | null
): {
  assessment: MicrophoneAssessment | null;
  fingerprint: MicrophoneFingerprint | null;
  shouldShowPrompt: boolean;
  isFirstTime: boolean;
  isLoading: boolean;
  prepareForAmbientScribeStart: (
    signal?: AbortSignal,
    micEnumerationPromise?: Promise<{ activeDeviceId: string | undefined }>
  ) => Promise<PrepareAmbientScribeStartResult>;
} {
  const [fingerprint, setFingerprint] = useState<MicrophoneFingerprint | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (cancelled) return;
        const device = devices.find(
          (d) => d.kind === "audioinput" && d.deviceId === deviceId
        );
        if (device) setFingerprint(createMicrophoneFingerprint(device));
        else setFingerprint(null);
      })
      .catch(() => {
        if (!cancelled) setFingerprint(null);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  // Verified mics are stored in localStorage — synchronous read, no loading state needed.
  const verifiedMics = useMemo(
    () => (isEnabled && staffId ? getVerifiedMicrophones(staffId) : []),
    // Re-read localStorage whenever staffId or isEnabled changes. The modal always
    // closes before recording starts so a fresh useMemo run captures the latest entry.
    [isEnabled, staffId]
  );

  const {
    data: lastScribes = [],
    isFetching: isScribesFetching,
    refetch: refetchLastScribes
  } = useGetAmbientScribesQuery(
    { limit: 2, staff: staffId, ordering: "-pk", query: LAST_SCRIBE_QUERY },
    {
      skip: !isEnabled || !staffId
    }
  );

  const isLoading = isScribesFetching;

  const assessment = useMemo<MicrophoneAssessment | null>(() => {
    if (!fingerprint) return null;
    return assessMicrophone(fingerprint, verifiedMics);
  }, [fingerprint, verifiedMics]);

  const isFirstTime =
    !isLoading && isEnabled && Boolean(staffId) && lastScribes.length === 0;

  const shouldShowPrompt = useMemo(
    () =>
      computeShouldShowMicUnverifiedPrompt({
        isEnabled,
        isLoading,
        assessment,
        staffId,
        lastScribes,
        micSetupCompletedAt: getMicSetupCompletedAt?.(),
        selectedMicDeviceId: deviceId
      }),
    // getMicSetupCompletedAt is a stable callback — calling it inside the memo
    // ensures the freshest localStorage value is used on each recompute.
    // deviceId is included explicitly because the new scribe mic-data rule compares
    // it directly (bypassing assessment), so assessment changing alone isn't enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEnabled, isLoading, assessment, staffId, lastScribes, getMicSetupCompletedAt, deviceId]
  );

  const prepareForAmbientScribeStart = useCallback(
    async (
      signal?: AbortSignal,
      micEnumerationPromise?: Promise<{
        activeDeviceId: string | undefined;
      }>
    ): Promise<PrepareAmbientScribeStartResult> => {
      const aborted = () => Boolean(signal?.aborted);

      let activeDeviceId: string | undefined;
      try {
        const result = micEnumerationPromise
          ? await micEnumerationPromise
          : await requestMicrophonePermissionForEnumeration(deviceId);
        activeDeviceId = result.activeDeviceId;
      } catch (cause) {
        throw new Error(
          "Microphone access was denied or is unavailable. Allow microphone access and try again.",
          { cause }
        );
      }

      if (aborted()) {
        return { cancelled: true };
      }

      let devices: MediaDeviceInfo[];
      try {
        devices = await navigator.mediaDevices.enumerateDevices();
      } catch {
        if (aborted()) return { cancelled: true };
        return {
          needsMicrophoneSetup: true,
          shouldShowMicUnverifiedPrompt: false
        };
      }

      if (aborted()) {
        return { cancelled: true };
      }

      let device = devices.find(
        (d) => d.kind === "audioinput" && d.deviceId === deviceId
      );
      // Pseudo device IDs ("default", "communications") are OS-level proxies — prefer
      // the real active device so fingerprinting matches what was saved during setup.
      if (device && PSEUDO_DEVICE_IDS.includes(device.deviceId.toLowerCase())) {
        device = undefined;
      }
      if (!device && activeDeviceId) {
        device = devices.find(
          (d) => d.kind === "audioinput" && d.deviceId === activeDeviceId
        );
      }

      if (!device) {
        if (!aborted()) {
          setFingerprint(null);
        }
        return {
          needsMicrophoneSetup: true,
          shouldShowMicUnverifiedPrompt: false
        };
      }

      const fp = createMicrophoneFingerprint(device);
      if (!aborted()) {
        setFingerprint(fp);
      } else {
        return { cancelled: true };
      }

      // Force a fresh fetch to get the latest backend scribe status.
      // The cached value may be stale (e.g. scribe was UPLOADING when last
      // cached, but has since transitioned to FAILED on the backend).
      let freshLastScribes = lastScribes;
      if (isEnabled && staffId) {
        try {
          freshLastScribes = await refetchLastScribes().unwrap();
        } catch {
          // Fall back to cached data if refetch fails (e.g. network error)
        }
      }

      if (aborted()) {
        return { cancelled: true };
      }

      // Read the latest verified mics directly from localStorage at call time
      // so we always use the freshest list even if another instance wrote since mount.
      const freshVerifiedMics =
        isEnabled && staffId ? getVerifiedMicrophones(staffId) : [];
      const freshAssessment = assessMicrophone(fp, freshVerifiedMics);
      const shouldShowMicUnverifiedPrompt =
        computeShouldShowMicUnverifiedPrompt({
          isEnabled,
          isLoading: false,
          assessment: freshAssessment,
          staffId,
          lastScribes: freshLastScribes,
          // Call the getter at invocation time so we always read the latest
          // localStorage value, even if another hook instance (e.g.
          // DeviceSettingsModal) wrote it after this component mounted.
          micSetupCompletedAt: getMicSetupCompletedAt?.(),
          // Raw device_id (pre-resolution) for direct comparison against
          // microphone_data from the last scribe.
          selectedMicDeviceId: deviceId
        });

      const microphoneFingerprint = fingerprintToInput(fp);

      // When device_id has drifted from the stored record (Safari/Firefox rotate device IDs),
      // save the fresh fingerprint to localStorage so the next session matches on exact device_id.
      // Guard prevents unnecessary writes for Chrome/Edge with stable device IDs.
      if (
        isEnabled &&
        staffId &&
        freshAssessment.verification === MicrophoneVerificationStatus.LikelyMatch &&
        fp.deviceId !== freshAssessment.matchedMicrophone?.device_id
      ) {
        saveVerifiedMicrophone(staffId, {
          ...microphoneFingerprint,
          verified_at: new Date().toISOString()
        });
      }

      return {
        shouldShowMicUnverifiedPrompt,
        microphoneFingerprint
      };
    },
    [
      deviceId,
      getMicSetupCompletedAt,
      isEnabled,
      lastScribes,
      refetchLastScribes,
      staffId
    ]
  );

  return {
    assessment,
    fingerprint,
    shouldShowPrompt,
    isFirstTime,
    isLoading,
    prepareForAmbientScribeStart
  };
}
